import { useDragControls, useAnimation, motion } from 'framer-motion';
import * as React from 'react';
import { useStyles } from '@sravni/react-utils';
import { Space } from '../../Space/src/Space.js';
import { EASINGS } from '../../Transition/src/_utils.js';
import '../../Transition/src/Fade.js';
import '../../Transition/src/ScaleFade.js';
import '../../Transition/src/SlideFade.js';
import '../../Transition/src/Collapse.js';
import { useWindowSize } from '../hooks/useWindowSize.js';
import modules_2fab7c71 from '../styles/Carousel.module.less.js';
import { Arrows } from './Arrows.js';

/**
 * @description A carousel component.
 *
 * @component
 * @example
 * ```jsx
 * <Carousel>
 *   <div>1</div>
 *   <div>2</div>
 * </Carousel>
 * ```
 */
const Carousel = React.forwardRef((props, ref) => {
    const { title, gradient, bottomControls, className, slidesClassName, gap = 20, children, ...restProps } = props;
    const dragControls = useDragControls();
    const cx = useStyles(modules_2fab7c71);
    const [containerWidth, setContainerWidth] = React.useState(0);
    const [slidesWidth, setSlidesWidth] = React.useState(0);
    const [singleSlideWidth, setSingleSlideWidth] = React.useState(0);
    const [isPrevDisabled, setIsPrevDisabled] = React.useState(true);
    const [isNextDisabled, setIsNextDisabled] = React.useState(false);
    const containerRef = React.useRef(null);
    const xPos = React.useRef(0);
    const isMoveAvailable = slidesWidth > containerWidth;
    const isHeaderVisible = title || (isMoveAvailable && !bottomControls);
    const headerClassNames = cx('header', {
        hidden: !title,
    });
    const slidesWrapperClassNames = cx('slides-wrapper', {
        gradient,
        [`left-gradient`]: !isPrevDisabled,
        [`right-gradient`]: !isNextDisabled,
    });
    const animation = useAnimation();
    const width = useWindowSize();
    const handlePrevClick = React.useCallback(async () => {
        const newXPosition = xPos.current + singleSlideWidth + gap;
        const constraint = 0;
        await animation.start({
            x: newXPosition > constraint ? constraint : newXPosition,
        }, {
            duration: 0.2,
            ease: EASINGS.easeOut,
        });
    }, [animation, singleSlideWidth, gap]);
    const handleNextClick = React.useCallback(async () => {
        const newXPosition = xPos.current - singleSlideWidth - gap;
        const constraint = containerWidth - slidesWidth;
        await animation.start({
            x: newXPosition < constraint ? constraint : newXPosition,
        }, {
            duration: 0.2,
            ease: EASINGS.easeOut,
        });
    }, [animation, singleSlideWidth, containerWidth, slidesWidth, gap]);
    const slides = React.useMemo(() => React.Children.map(children, (child) => (React.createElement("div", { style: { width: child.props.style?.width, height: child.props.style?.height } }, child))), [children]);
    const slidesStyle = React.useMemo(() => ({
        // eslint-disable-next-line @typescript-eslint/naming-convention
        '--gap': `${gap}px`,
        x: 0,
    }), [gap]);
    const handleUpdate = React.useCallback((latest) => {
        xPos.current = Number(latest.x);
        setIsPrevDisabled(xPos.current >= 0);
        setIsNextDisabled(xPos.current <= containerWidth - slidesWidth);
    }, [containerWidth, slidesWidth]);
    const preventPropagation = React.useCallback((event) => {
        event.stopPropagation();
        dragControls.start(event);
    }, [dragControls]);
    React.useEffect(() => {
        if (!containerRef.current) {
            return;
        }
        const slider = containerRef.current;
        const newSlidesWidth = Array.from(slider.children).reduce((acc, slide) => acc + slide.clientWidth + gap, 0) - gap;
        setIsNextDisabled(Number(xPos?.current) <= containerWidth - newSlidesWidth);
        setSlidesWidth(newSlidesWidth);
        setContainerWidth(slider.clientWidth);
        setSingleSlideWidth(slider.children[0]?.clientWidth);
    }, [width, slides, containerWidth, gap]);
    return (React.createElement("div", { ref: ref, "data-qa": "Carousel", className: cx(className, 'container'), ...restProps },
        isHeaderVisible && (React.createElement(Space, { justify: "space-between", className: headerClassNames },
            React.createElement("div", { className: cx('title') }, title),
            isMoveAvailable && !bottomControls && (React.createElement(Arrows, { isPrevDisabled: isPrevDisabled, isNextDisabled: isNextDisabled, onPrevClick: handlePrevClick, onNextClick: handleNextClick })))),
        React.createElement("div", { className: slidesWrapperClassNames, onPointerDownCapture: preventPropagation },
            React.createElement(motion.div, { "data-qa": "Slides", ref: containerRef, drag: isMoveAvailable ? 'x' : false, animate: animation, initial: false, style: slidesStyle, dragControls: dragControls, dragConstraints: {
                    left: containerWidth - slidesWidth,
                    right: 0,
                }, onUpdate: handleUpdate, className: cx('slides', slidesClassName) }, slides)),
        isMoveAvailable && bottomControls && (React.createElement("div", { className: cx('bottom-controls') },
            React.createElement(Arrows, { isPrevDisabled: isPrevDisabled, isNextDisabled: isNextDisabled, onPrevClick: handlePrevClick, onNextClick: handleNextClick })))));
});
Carousel.displayName = 'Carousel';

export { Carousel };
//# sourceMappingURL=Carousel.js.map
